import React, { useRef, useEffect, useState } from 'react';
import {
  IconRobot,
  IconX,
  IconTrash,
  IconSend,
  IconLoader,
  IconSparkles,
  IconCommand,
  IconCheck,
  IconBan,
  IconTool,
  IconArrowRight,
  IconPlayerStop,
  IconCircleCheck,
  IconCircleX,
  IconCircleDashed,
  IconPlayerPlay
} from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAgentChat } from '../../contexts/AgentChatContext';

const SUGGESTIONS = [
  'Show all active campaigns',
  'List top creators by followers',
  'Get workflow overview',
  'Revenue stats this month',
  'Show dashboard stats',
  'List brands',
  'Check Discord bot status',
  'Twitter follower stats',
];

function formatTime(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function AgentBadge({ agent }) {
  if (!agent) return null;
  const name = agent.replace('Agent', '');
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-accent/10 text-accent text-[10px] font-medium">
      <IconArrowRight className="h-2.5 w-2.5" />
      {name}
    </span>
  );
}

function ToolBadges({ toolCalls, completedTools }) {
  if (!toolCalls?.length) return null;
  // Deduplicate
  const unique = [...new Set(toolCalls)];
  // Build a lookup for durations from completedTools
  const durationMap = {};
  if (completedTools) {
    for (const t of completedTools) {
      durationMap[t.name] = t.durationMs;
    }
  }
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {unique.map((name, i) => (
        <span key={i} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-surface-3 text-[9px] text-text-tertiary font-mono">
          <IconTool className="h-2.5 w-2.5" />
          {name}
          {durationMap[name] != null && (
            <span className="text-text-tertiary/60 ml-0.5">{durationMap[name]}ms</span>
          )}
        </span>
      ))}
    </div>
  );
}

function ApprovalCard({ approval, onApprove, onReject }) {
  return (
    <div className="ui-motion-enter bg-accent-muted border border-accent/30 p-3 mb-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <IconTool className="h-3.5 w-3.5 text-accent" />
        <span className="text-xs font-semibold text-accent">Approval Required</span>
      </div>
      <p className="text-xs text-text-secondary mb-1">
        <span className="font-mono font-medium">{approval.tool}</span>
      </p>
      {approval.args && Object.keys(approval.args).length > 0 && (
        <pre className="text-[10px] text-text-tertiary bg-surface-1 p-2 mb-2 overflow-x-auto max-h-20">
          {JSON.stringify(approval.args, null, 2)}
        </pre>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => onApprove(approval.approvalId)}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-accent/20 text-accent text-xs font-medium hover:bg-accent/30"
        >
          <IconCheck className="h-3 w-3" /> Approve
        </button>
        <button
          onClick={() => onReject(approval.approvalId)}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-400/20 text-gray-400 text-xs font-medium hover:bg-gray-400/30"
        >
          <IconBan className="h-3 w-3" /> Reject
        </button>
      </div>
    </div>
  );
}

function SkillProgressCard({ skill }) {
  if (!skill) return null;

  const stepIcon = (status) => {
    switch (status) {
      case 'completed': return <IconCircleCheck className="h-3.5 w-3.5 text-accent" />;
      case 'running': return <IconLoader className="h-3.5 w-3.5 text-accent animate-spin" />;
      case 'failed': return <IconCircleX className="h-3.5 w-3.5 text-gray-400" />;
      case 'failed_optional': return <IconCircleX className="h-3.5 w-3.5 text-accent" />;
      case 'skipped': return <IconCircleDashed className="h-3.5 w-3.5 text-text-tertiary" />;
      default: return <IconCircleDashed className="h-3.5 w-3.5 text-text-tertiary" />;
    }
  };

  const completedCount = skill.steps.filter(s => s.status === 'completed').length;

  return (
    <div className="ui-motion-enter bg-accent-muted border border-accent/20 p-3 mb-3">
      <div className="flex items-center gap-1.5 mb-2">
        <IconPlayerPlay className="h-3.5 w-3.5 text-accent" />
        <span className="text-xs font-semibold text-accent">
          {skill.completed ? (skill.success ? 'Workflow Complete' : 'Workflow Failed') : 'Running Workflow'}
        </span>
        <span className="text-[10px] text-text-tertiary ml-auto">
          {completedCount}/{skill.totalSteps}
        </span>
      </div>
      <div className="space-y-1">
        {skill.steps.map((step, i) => (
          <div key={step.name || i} className="flex items-center gap-2">
            {stepIcon(step.status)}
            <span className={`text-xs ${step.status === 'running' ? 'text-text-primary font-medium' : 'text-text-secondary'}`}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatMessage({ message }) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className="max-w-[90%]">
        {!isUser && (
          <div className="flex items-center gap-1.5 mb-1">
            <IconRobot className="h-3 w-3 text-accent" />
            <span className="text-[10px] font-medium text-accent uppercase tracking-wider">Agent</span>
            {message.activeAgent && <AgentBadge agent={message.activeAgent} />}
            {message.toolCalls?.length > 0 && (
              <span className="text-[10px] text-text-tertiary">
                &middot; {message.toolCalls.length} tool{message.toolCalls.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
        <div
          className={`px-3.5 py-2.5 text-sm leading-relaxed ${
            isUser
              ? 'bg-accent text-white whitespace-pre-wrap'
              : message.isError
                ? 'bg-gray-400/10 text-gray-400 whitespace-pre-wrap'
                : 'bg-surface-2 text-text-primary agent-markdown'
          }`}
        >
          {isUser || message.isError ? (
            message.content
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1 className="text-base font-bold mt-3 mb-1.5 first:mt-0">{children}</h1>,
                h2: ({ children }) => <h2 className="text-sm font-bold mt-2.5 mb-1 first:mt-0">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1 first:mt-0">{children}</h3>,
                p: ({ children }) => <p className="mb-1.5 last:mb-0">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-4 mb-1.5 space-y-0.5">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-4 mb-1.5 space-y-0.5">{children}</ol>,
                li: ({ children }) => <li className="text-sm">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                code: ({ inline, children }) =>
                  inline
                    ? <code className="px-1 py-0.5 bg-surface-3 text-accent text-xs font-mono">{children}</code>
                    : <code className="block bg-surface-3 p-2 my-1.5 text-xs font-mono overflow-x-auto">{children}</code>,
                pre: ({ children }) => <pre className="bg-surface-3 p-2 my-1.5 text-xs overflow-x-auto">{children}</pre>,
                table: ({ children }) => (
                  <div className="overflow-x-auto my-1.5">
                    <table className="w-full text-xs border-collapse">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-surface-3">{children}</thead>,
                th: ({ children }) => <th className="text-left px-2 py-1 font-semibold border-b border-glass-border">{children}</th>,
                td: ({ children }) => <td className="px-2 py-1 border-b border-glass-border/50">{children}</td>,
                blockquote: ({ children }) => <blockquote className="border-l-2 border-accent/40 pl-2 my-1.5 text-text-secondary">{children}</blockquote>,
                hr: () => <hr className="border-glass-border my-2" />,
                a: ({ href, children }) => <a href={href} className="text-accent underline" target="_blank" rel="noopener noreferrer">{children}</a>,
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
          {message.streaming && (
            <span className="inline-block w-1 h-4 bg-accent ml-0.5 animate-pulse" />
          )}
        </div>
        <ToolBadges toolCalls={message.toolCalls} />
        <div className={`text-[10px] text-text-tertiary mt-0.5 ${isUser ? 'text-right' : 'text-left'}`}>
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
}

function ElapsedTimer({ startTime }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!startTime) return;
    const interval = setInterval(() => setElapsed(Date.now() - startTime), 100);
    return () => clearInterval(interval);
  }, [startTime]);
  if (!startTime) return null;
  const secs = (elapsed / 1000).toFixed(1);
  return (
    <span className="text-[10px] text-text-tertiary tabular-nums">
      {secs}s
    </span>
  );
}

function ActivityIndicator({ activeAgent, activityPhase, activeTools, completedTools, requestStartTime }) {
  const phaseLabel = (() => {
    switch (activityPhase) {
      case 'thinking': return 'Starting...';
      case 'routing': return activeAgent ? `Routing to ${activeAgent.replace('Agent', '')}` : 'Routing...';
      case 'calling_tools': return 'Running tools';
      case 'streaming': return activeAgent ? `${activeAgent.replace('Agent', '')} responding` : 'Responding';
      default: return 'Thinking...';
    }
  })();

  return (
    <div className="flex justify-start mb-3">
      <div className="max-w-[90%]">
        <div className="flex items-center gap-1.5 mb-1">
          <IconRobot className="h-3 w-3 text-accent" />
          <span className="text-[10px] font-medium text-accent uppercase tracking-wider">Agent</span>
          {activeAgent && <AgentBadge agent={activeAgent} />}
          <ElapsedTimer startTime={requestStartTime} />
        </div>
        <div className="bg-surface-2 px-4 py-3">
          {/* Phase label */}
          <div className="flex items-center gap-1.5 mb-1">
            <IconLoader className="h-3.5 w-3.5 text-accent animate-spin" />
            <span className="text-xs text-text-secondary font-medium">{phaseLabel}</span>
          </div>
          {/* Active tools with spinner */}
          {activeTools && activeTools.length > 0 && (
            <div className="space-y-0.5 mt-1.5">
              {activeTools.map((t, i) => (
                <div key={t.name + i} className="flex items-center gap-1.5 pl-1">
                  <IconLoader className="h-2.5 w-2.5 text-accent animate-spin" />
                  <span className="text-[10px] text-text-tertiary font-mono">{t.name}</span>
                </div>
              ))}
            </div>
          )}
          {/* Completed tools with checkmark */}
          {completedTools && completedTools.length > 0 && (
            <div className="space-y-0.5 mt-1">
              {completedTools.map((t, i) => (
                <div key={t.name + i} className="flex items-center gap-1.5 pl-1">
                  {t.error ? (
                    <IconCircleX className="h-2.5 w-2.5 text-gray-400" />
                  ) : (
                    <IconCircleCheck className="h-2.5 w-2.5 text-accent" />
                  )}
                  <span className="text-[10px] text-text-tertiary font-mono">{t.name}</span>
                  {t.durationMs != null && (
                    <span className="text-[9px] text-text-tertiary/60">{t.durationMs}ms</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function AgentSidebar() {
  const {
    messages, loading, streaming, sidebarOpen, activeAgent, pendingApprovals, activeSkill,
    activityPhase, activeTools, completedTools, requestStartTime,
    closeSidebar, sendMessage, clearChat, stopStreaming, approveAction, rejectAction
  } = useAgentChat();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom on new messages or activity changes
  useEffect(() => {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    messagesEndRef.current?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' });
  }, [messages, loading, pendingApprovals, activeSkill, activeTools, completedTools]);

  // Focus input when sidebar opens
  useEffect(() => {
    if (sidebarOpen) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [sidebarOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    sendMessage(input);
    setInput('');
  };

  if (!sidebarOpen) return null;

  // Show activity indicator when loading in a pre-streaming phase
  // Once tokens start flowing (phase='streaming'), the message bubble + cursor handle visual feedback
  const showActivityIndicator = loading && activityPhase != null && activityPhase !== 'streaming';

  return (
    <div data-side="right" className="ui-motion-panel w-full sm:w-[380px] fixed inset-0 sm:static sm:inset-auto z-50 sm:z-auto flex-shrink-0 border-l border-glass-border bg-surface-1 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-glass-border flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-accent flex items-center justify-center">
            <IconRobot className="h-4 w-4 text-white" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-text-primary">AI Agent</h4>
            <p className="text-[10px] text-text-tertiary">
              {activeAgent ? activeAgent.replace('Agent', ' Specialist') : 'Platform assistant'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {streaming && (
            <button
              onClick={stopStreaming}
              className="p-1.5 hover:bg-surface-2 text-accent"
              title="Stop streaming"
            >
              <IconPlayerStop className="h-4 w-4" />
            </button>
          )}
          {messages.length > 0 && (
            <button
              onClick={clearChat}
              className="p-1.5 hover:bg-surface-2 text-text-tertiary hover:text-text-secondary"
              title="Clear chat"
            >
              <IconTrash className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={closeSidebar}
            className="p-1.5 hover:bg-surface-2 text-text-tertiary hover:text-text-secondary"
            title="Close sidebar"
          >
            <IconX className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center py-8">
            <div className="w-14 h-14 bg-accent-muted flex items-center justify-center mb-3">
              <IconSparkles className="h-7 w-7 text-accent" />
            </div>
            <p className="text-sm font-medium text-text-primary mb-1">How can I help?</p>
            <p className="text-xs text-text-tertiary text-center mb-5 max-w-[250px]">
              I can manage campaigns, creators, payments, Discord, Twitter, brands, and more.
            </p>
            <div className="w-full space-y-1.5">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { sendMessage(s); }}
                  className="w-full text-left px-3 py-2 bg-surface-2 hover:bg-surface-3 text-sm text-text-secondary hover:text-text-primary"
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-1.5 text-[10px] text-text-tertiary">
              <IconCommand className="h-3 w-3" />
              <span>Tip: Use <kbd className="px-1 bg-surface-2 border border-glass-border text-[9px]">Cmd+K</kbd> for quick commands</span>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, i) => {
              // Hide empty streaming placeholder when ActivityIndicator is visible
              if (showActivityIndicator && msg.streaming && !msg.content) return null;
              return <ChatMessage key={msg.id || i} message={msg} />;
            })}
            {/* Skill progress */}
            {activeSkill && <SkillProgressCard skill={activeSkill} />}
            {/* Approval cards */}
            {pendingApprovals.map(approval => (
              <ApprovalCard
                key={approval.approvalId}
                approval={approval}
                onApprove={approveAction}
                onReject={rejectAction}
              />
            ))}
            {showActivityIndicator && (
              <ActivityIndicator
                activeAgent={activeAgent}
                activityPhase={activityPhase}
                activeTools={activeTools}
                completedTools={completedTools}
                requestStartTime={requestStartTime}
              />
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex-shrink-0 px-3 pb-3 pt-2 border-t border-glass-border">
        <div className="flex items-center gap-2 bg-surface-2 px-3 py-1">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask anything..."
            className="flex-1 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary outline-none py-2"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={!input.trim() || loading}
            className="p-1.5 text-accent hover:bg-accent/10 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {loading ? (
              <IconLoader className="h-4 w-4 animate-spin" />
            ) : (
              <IconSend className="h-4 w-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
